import { Component, OnInit } from '@angular/core';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {UserApiTokenMetadata} from '@iana-rzms/bff-sdk';
import {UserService} from '../../../services/user.service';
import {UserVO} from '../../../generate/UserVO';
import {AuthService} from '../../../services/auth.service';

@Component({
    selector: 'api-tokens',
    templateUrl: './api-tokens.component.html',
    styleUrls: ['./api-tokens.component.scss'],
    standalone: false
})
export class ApiTokensComponent implements OnInit {
  get successMessage(): string {
    return this._successMessage;
  }

  set successMessage(value: string) {
    this._errorMessage = undefined;
    this._successMessage = value;
  }

  get errorMessage(): string {
    this._successMessage = undefined;
    return this._errorMessage;
  }

  set errorMessage(value: string) {
    this._errorMessage = value;
  }

  private _successMessage: string;
  private _errorMessage: string;

  modalContent: string = null;
  currentUser: UserVO;
  apiTokenRandomID: string;
  require2FARandomID: string;
  editTokenNameRandomID: string;
  confirmationRandomID: string;
  apiTokens: UserApiTokenMetadata[] = [];
  selectedApiToken: UserApiTokenMetadata;
  newlyCreatedToken: string;

  constructor(
      private userService: UserService,
      private authService: AuthService,
      private ngxSmartModalService: NgxSmartModalService
  ) { }

  ngOnInit(): void {
    this.apiTokenRandomID = Math.random().toString(36).substring(7);
    this.editTokenNameRandomID = Math.random().toString(36).substring(7);
    this.confirmationRandomID = Math.random().toString(36).substring(7);
    this.require2FARandomID = Math.random().toString(36).substring(7);
    this.currentUser = this.authService.currentUserAuthVO();
    this.authService.currentUserSubject.subscribe((user) => {
      this.currentUser = user;
    });
    this.getExistingApiTokens();
  }

  clickAddApiToken() {
    if (!this.currentUser?.totpEnabled) {
      this.modalContent = 'Two-Factor Authentication (2FA) must be enabled before adding an API token.';
      this.ngxSmartModalService.open(this.require2FARandomID);
    } else {
      this.openCreateApiTokenModal();
    }
  }

  openCreateApiTokenModal() {
    if (this.apiTokens.length < 10) {
      this.modalContent = 'Creating token...';
      this.ngxSmartModalService.open(this.apiTokenRandomID);

      if (this.currentUser) {
        this.userService.createApiToken(this.currentUser.id).subscribe((token) => {
          this.newlyCreatedToken = token.jwt;
          this.modalContent = 'Token successfully generated. Save this token now, as it will not be accessible after closing this dialog.';
          this.getExistingApiTokens();
        }, error => {
          console.log(error);
          const errorMessage = error.message.errorMessages ? error.message.errorMessages[0] : '';
          console.log('Unable to create API token. ' + errorMessage);
          this.modalContent = errorMessage;
        });
      }
    }
  }

  copyAPIToken() {
    this.modalContent = 'API token copied';
    navigator.clipboard.writeText(this.newlyCreatedToken);
  }

  openEditApiTokenNameModal(apiToken: UserApiTokenMetadata) {
    this.selectedApiToken = apiToken;
    this.ngxSmartModalService.open(this.editTokenNameRandomID);
  }

  editAPITokenName(name: string) {
    this.userService.renameApiToken(this.selectedApiToken?.sessionId, name).subscribe(() => {
      this.getExistingApiTokens();
    });
    this.ngxSmartModalService.close(this.editTokenNameRandomID);
  }

  removeAPIToken(token: UserApiTokenMetadata) {
    this.selectedApiToken = token;
    this.ngxSmartModalService.open(this.confirmationRandomID);
  }

  isTokenExpired(token: UserApiTokenMetadata): boolean {
    const now = new Date();
    const expiryTime = new Date(token.expiryTime);
    return now > expiryTime;
  }

  private getExistingApiTokens() {
    if (this.currentUser) {
      this.userService.getExistingApiTokens(this.currentUser.id).subscribe((tokens) => {
        this.apiTokens = [...tokens];
      });
    }
  }

  revokeToken() {
    const tokenSessionId = this.selectedApiToken?.sessionId;
    const tokenSuffix = this.selectedApiToken?.tokenSuffix;
    this.userService.revokeApiToken(tokenSessionId,tokenSuffix).subscribe(() => {
      this.getExistingApiTokens();
    });
    this.ngxSmartModalService.close(this.confirmationRandomID);
  }
}
